/**
 * Pure data constants for the feature-flag system.
 *
 * Extracted from `useFeatures.ts` so that the pure-function permission
 * helpers (`src/features/permissions.ts`) and tests can import them
 * without dragging in React, Supabase or React Query.
 *
 * `useFeatures.ts` re-exports these for backwards compatibility — every
 * existing import (`from '@/hooks/useFeatures'`) keeps working.
 */

// All feature keys
export const ALL_FEATURE_KEYS = [
  // Nav
  'rechnungen','ernaehrungsplaene','sitzungsaufnahme','telemedizin',
  'warteliste','gruppen','integrationen',
  // Patient tabs
  'ernaehrungsplan_patient','tagebuch','klinik_modul','klinik_mna_must',
  'klinik_laborwerte','klinik_iddsi','klinik_pflege','klinik_glim',
  'klinik_stationsuebersicht','klinik_konsile','klinik_kuechen_export','klinik_kpi_dashboard','klinik_nutriscreen','laborwerte',
  // New feature flags
  'medikamente','diaetanamnese','aufgaben','fragebögen','portal_uploads',
  'outcome_tracking','multi_therapeuten','email_benachrichtigungen',
  'ki_zeno','bls_ernaehrungsanalyse',
  // Neue Features (Ebene 1)
  'rezeptanalyse','fhir_export',
  // Phase 1
  'gkv_abrechnung',
  // Phase 2.4
  'klinik_entlassbrief_ki',
  // Phase 3 — Specialist modules
  'klinik_its','klinik_onkologie','klinik_geriatrie',
  // KI-nativ Plattform (siehe docs/AI_NATIVE_PLAN.md). Gated, weil
  // Hintergrund-Watcher zusätzliche KI-Kosten verursachen können.
  'ai_native',
  // Flow-Redesign: aufgabenfokussiertes Interface (Timeline / Ambient
  // Voice / Linked Evidence). Seit UX-Audit Phase 4c Default-ON als
  // gemeinsamer Basis-UI-Modus (analog `ai_native`), pro Org via Settings
  // abschaltbar (dann klassische UI); Einzelnutzer-Escape via `?view=classic`.
  'ui_flow_mode',
  // AI-First Phase 4: Chat-Feed-Modus — Suggestions als Smart Cards im
  // Feed statt als Bell-Popover. Morning Briefing automatisch einmal täglich.
  // Default OFF in Phase 4; Default-Flip folgt in Phase 6.
  'ui_ai_first',
] as const;

export type Feature = typeof ALL_FEATURE_KEYS[number];

/**
 * Public market MVP boundary.
 *
 * These features may exist in the codebase or in legacy/test orgs, but the
 * public product must not expose them because they can be read as clinical
 * pre-decision support under MDR: risk scoring, diagnosis support, therapy
 * suggestions, prioritisation, coding support or automated clinical letters.
 */
export const PUBLIC_MVP_DISABLED_FEATURES: readonly Feature[] = [
  'ai_native',
  'ui_ai_first',
  'klinik_modul',
  'klinik_mna_must',
  'klinik_laborwerte',
  'klinik_iddsi',
  'klinik_pflege',
  'klinik_glim',
  'klinik_stationsuebersicht',
  'klinik_konsile',
  'klinik_kuechen_export',
  'klinik_kpi_dashboard',
  'klinik_nutriscreen',
  'klinik_entlassbrief_ki',
  'klinik_its',
  'klinik_onkologie',
  'klinik_geriatrie',
  'fhir_export',
  'integrationen',
  'outcome_tracking',
  'bls_ernaehrungsanalyse',
  'rezeptanalyse',
  'ki_zeno',
];

// Modul-Typ: basis (immer dabei), praxis (Praxis-Kern), klinik (Klinik-Kern), frei (Portfolio-Baustein)
export type ModulTyp = 'basis' | 'praxis' | 'klinik' | 'frei';

// Kern-Pakete: Basis ist immer dabei, dann Richtung Praxis oder Klinik,
// freie Funktionen werden von Icarus individuell pro Kunde zugeordnet.
//
export const FEATURE_PLANS: Record<string, Feature[]> = {
  basis: [
    'ernaehrungsplaene','diaetanamnese','aufgaben','fragebögen',
    'ernaehrungsplan_patient','tagebuch',
    // Phase 4c: Flow-Modus ist Default-Rückgrat (gemeinsamer Basis-UI-Modus).
    'ui_flow_mode',
  ],
  praxis: [
    // Basis +
    'ernaehrungsplaene','diaetanamnese','aufgaben','fragebögen',
    'ernaehrungsplan_patient','tagebuch',
    'ui_flow_mode',
    // Praxis-Kern
    'rechnungen','warteliste','gruppen','gkv_abrechnung',
  ],
  klinik: [
    // Basis +
    'ernaehrungsplaene','diaetanamnese','aufgaben','fragebögen',
    'ernaehrungsplan_patient','tagebuch',
    'ui_flow_mode',
    // Klinik-Kern
    'klinik_modul','klinik_mna_must','klinik_laborwerte',
    'klinik_iddsi','klinik_pflege','klinik_glim',
    'klinik_stationsuebersicht','klinik_konsile','klinik_kuechen_export','klinik_kpi_dashboard',
    'klinik_nutriscreen',
  ],
};

// Freie Funktionen – können sowohl Klinik als auch Praxis zugeordnet werden.
// Icarus stellt diese Portfolio-artig pro Kunde zusammen (Bezahlung pro Funktion).
// `ai_native` und `ui_flow_mode` sind Basis-Features (Default-ON) und gehören
// NICHT mehr hierhin — beide sind pro Org via Settings abschaltbar.
export const FREIE_FUNKTIONEN: Feature[] = [
  'sitzungsaufnahme','telemedizin','email_benachrichtigungen',
  'ki_zeno','portal_uploads','outcome_tracking','laborwerte','medikamente',
  'integrationen','multi_therapeuten','bls_ernaehrungsanalyse',
  'rezeptanalyse','fhir_export',
  'klinik_entlassbrief_ki',
  'klinik_its',
  'klinik_onkologie',
  'klinik_geriatrie',
];

// Feature metadata for UI
export type FeatureMeta = {
  key: Feature;
  label: string;
  description: string;
  category: string;
  requires?: Feature[];
  modulTyp: ModulTyp;
  /**
   * Phase 5 — Module Browser fields.
   *
   * `price_monthly`: Monthly add-on price in EUR.
   *   `null` / `undefined` = included in base plan, no extra charge.
   *   Per D-4: paid-module toggles only write audit_log + show confirm
   *   dialog; no actual Stripe charge in Phase 5.
   */
  price_monthly?: number | null;
  /**
   * URLs for preview screenshots shown in a lightbox on the Module Card.
   * May be empty in Phase 5 (lightbox button visible but stub).
   */
  preview_images?: string[];
  /**
   * Audience recommendation. Used to surface a "Empfohlen für dich"
   * badge when the user's org type matches.
   */
  recommended_for?: ('praxis' | 'klinik' | 'hub')[];
  /**
   * Short-lived "Neu" badge on the module card. Reset periodically
   * (out of scope for Phase 5 — simply set to true for new modules).
   */
  is_new?: boolean;
  /**
   * Lucide icon name as a string for dynamic rendering in the Module
   * Browser card. Falls back to "Sparkles" when unset or unknown.
   */
  icon?: string;
};

/**
 * Follow-up 3: leitet eine sinnvolle Audience-Empfehlung aus `modulTyp`
 * ab, wenn das Modul (noch) kein explizites `recommended_for` pflegt.
 * Das hält die FEATURE_META-Liste kompakt — pro-Modul-Tuning ist
 * weiterhin möglich, indem `recommended_for` explizit gesetzt wird.
 *
 *   modulTyp 'praxis' →  ['praxis']
 *   modulTyp 'klinik' →  ['klinik']
 *   modulTyp 'basis'  →  ['praxis', 'klinik']
 *   modulTyp 'frei'   →  ['praxis', 'klinik']
 *
 * Modul-Keys mit "klinik_"-Prefix werden zusätzlich als klinik-only
 * behandelt, weil sie fachlich nur dort Sinn ergeben.
 */
export function resolveRecommendedFor(
  meta: Pick<FeatureMeta, 'modulTyp' | 'recommended_for' | 'key'>,
): ReadonlyArray<'praxis' | 'klinik' | 'hub'> {
  if (meta.recommended_for && meta.recommended_for.length > 0) {
    return meta.recommended_for;
  }
  if (meta.key.startsWith('klinik_') || meta.modulTyp === 'klinik') {
    return ['klinik'];
  }
  if (meta.modulTyp === 'praxis') {
    return ['praxis'];
  }
  // basis + frei + hub
  return ['praxis', 'klinik'];
}

export const FEATURE_META: FeatureMeta[] = [
  // ─── Basis (immer dabei) ───
  { key: 'ernaehrungsplaene', label: 'Ernährungspläne', description: 'Vorlagen-Bibliothek und individuelle Pläne', category: 'Basis', modulTyp: 'basis' },
  { key: 'diaetanamnese', label: 'Strukturierte Diätanamnese', description: '24h-Recall, FFQ, Mahlzeitenstruktur, Kontext', category: 'Basis', modulTyp: 'basis' },
  { key: 'aufgaben', label: 'Aufgaben / Homework', description: 'Hausaufgaben zuweisen, Erledigung nachverfolgen', category: 'Basis', modulTyp: 'basis' },
  { key: 'fragebögen', label: 'Fragebogen-Engine', description: 'Standardisierte Fragebögen (EQ-5D, Therapiezufriedenheit…)', category: 'Basis', modulTyp: 'basis' },
  { key: 'ernaehrungsplan_patient', label: 'Ernährungsplan (Portal)', description: 'Ernährungsplan im Patientenportal sichtbar', category: 'Basis', requires: ['ernaehrungsplaene'], modulTyp: 'basis',
    preview_images: ['/screenshots/portal-home.svg'] },
  { key: 'tagebuch', label: 'Ernährungstagebuch', description: 'Patienten dokumentieren Mahlzeiten im Portal', category: 'Basis', modulTyp: 'basis',
    preview_images: ['/screenshots/portal-tagebuch.svg'] },
  // ─── Praxis-Kern ───
  { key: 'rechnungen', label: 'Rechnungsstellung', description: 'GKV/PKV Rechnungen, Mahnwesen, Export', category: 'Praxis', modulTyp: 'praxis' },
  { key: 'warteliste', label: 'Warteliste', description: 'Wartelistenverwaltung mit Prioritäten', category: 'Praxis', modulTyp: 'praxis' },
  { key: 'gruppen', label: 'Gruppentherapie', description: 'ET4/ET5/ET6 Gruppensitzungen mit Anwesenheit', category: 'Praxis', requires: ['warteliste'], modulTyp: 'praxis' },
  { key: 'gkv_abrechnung', label: 'GKV-Abrechnung', description: 'Abrechnung mit gesetzlichen Krankenkassen', category: 'Praxis', modulTyp: 'praxis' },
  // ─── Klinik-Kern ───
  { key: 'klinik_modul', label: 'Klinik-Basis', description: 'NRS-2002 inkl. Vor-Screening, Stationsdoku, OPS-Codes', category: 'Klinik', modulTyp: 'klinik' },
  { key: 'klinik_mna_must', label: 'MNA/MUST Screening', description: 'MNA für Geriatrie, MUST für allg. Stationen', category: 'Klinik', requires: ['klinik_modul'], modulTyp: 'klinik' },
  { key: 'klinik_laborwerte', label: 'Laborwerte (klinisch)', description: 'Laborwerte-Dokumentation im Stationskontext', category: 'Klinik', requires: ['klinik_modul'], modulTyp: 'klinik' },
  { key: 'klinik_iddsi', label: 'IDDSI Assessment', description: 'Schlucksicherheitsassessment mit Kostform-Empfehlung', category: 'Klinik', requires: ['klinik_modul'], modulTyp: 'klinik' },
  { key: 'klinik_pflege', label: 'Pflege-Schnelldoku', description: 'Pflegedokumentation für Ernährung (Trinkmenge, Intake)', category: 'Klinik', requires: ['klinik_modul'], modulTyp: 'klinik' },
  { key: 'klinik_glim', label: 'GLIM-Diagnose', description: 'GLIM-Kriteriensatz für strukturierte Mangelernährungsdiagnose mit ICD-10-GM-Codierung', category: 'Klinik', requires: ['klinik_modul'], modulTyp: 'klinik' },
  { key: 'klinik_stationsuebersicht', label: 'Stationsübersicht', description: 'Arbeitsbildschirm für das Ernährungsteam: alle stationären Patient·innen mit Ampel-Status (NRS-2002 + GLIM).', category: 'Klinik', requires: ['klinik_modul'], modulTyp: 'klinik' },
  { key: 'klinik_konsile', label: 'Konsil-Inbox', description: 'Cross-patient Arbeitsliste für Konsilanfragen an das Ernährungsteam mit Priorität, Zuweisung und Abschluss-Workflow.', category: 'Klinik', requires: ['klinik_modul'], modulTyp: 'klinik' },
  { key: 'klinik_kuechen_export', label: 'Küchen-CSV-Export', description: 'Kostform-Anordnungen + Allergien + Unverträglichkeiten als CSV-Export für das Küchensystem.', category: 'Klinik', requires: ['klinik_modul'], modulTyp: 'klinik' },
  { key: 'klinik_kpi_dashboard', label: 'KPI-Dashboard §110a', description: 'KHZG FTB-4 Nachweisführung: NRS-Quote, GLIM-Quote, Konsil-TAT, Entlassbrief-Quote, OPS-Coverage.', category: 'Klinik', requires: ['klinik_modul'], modulTyp: 'klinik' },
  { key: 'klinik_nutriscreen', label: 'NutriScreen', description: 'Patientenseitige Mangelernährungs-Screenings (MUST/NRS-2002/MNA-SF/SGA/PG-SGA/STRONGkids) per QR/NFC mit Triage-Inbox.', category: 'Klinik', requires: ['klinik_modul'], modulTyp: 'klinik' },
  // ─── Freie Funktionen (Portfolio-Bausteine, individuell zuordenbar) ───
  {
    key: 'sitzungsaufnahme', label: 'Sitzungsaufnahme (KI)',
    description: 'Audio-Transkription mit Whisper + GNCP-Autofill',
    category: 'Freie Funktionen', modulTyp: 'frei',
    price_monthly: 15, recommended_for: ['praxis'], is_new: false, icon: 'Mic',
  },
  {
    key: 'telemedizin', label: 'Telemedizin',
    description: 'Video-Konsultationen direkt in NutriFlow',
    category: 'Freie Funktionen', modulTyp: 'frei',
    price_monthly: 20, recommended_for: ['praxis'], is_new: false, icon: 'Video',
  },
  { key: 'email_benachrichtigungen', label: 'E-Mail-Benachrichtigungen', description: 'Automatische Erinnerungen für Termine, Aufgaben, Nachrichten', category: 'Freie Funktionen', modulTyp: 'frei' },
  { key: 'ki_zeno', label: 'Nutri Dokumentationsassistent', description: 'KI-Dokumentationsassistent im Patientenportal (Art. 22 DSGVO)', category: 'Freie Funktionen', modulTyp: 'frei' },
  { key: 'portal_uploads', label: 'Dokumenten-Upload', description: 'Patienten laden Laborbefunde & Fotos hoch', category: 'Freie Funktionen', modulTyp: 'frei' },
  { key: 'outcome_tracking', label: 'Outcome-Tracking', description: 'Therapieerfolg messen: NRS-Verlauf, Gewicht, Labortrends', category: 'Freie Funktionen', modulTyp: 'frei' },
  { key: 'laborwerte', label: 'Laborwerte (ambulant)', description: 'Laborwerte-Tab auch ohne Klinik-Modul', category: 'Freie Funktionen', modulTyp: 'frei' },
  { key: 'medikamente', label: 'Medikamentenliste', description: 'Aktuelle Medikation je Patient dokumentieren (ambulant & stationär)', category: 'Freie Funktionen', modulTyp: 'frei' },
  { key: 'integrationen', label: 'KIS/PVS Schnittstellen', description: 'Webhook-Events an KIS, PVS, Abrechnungszentren', category: 'Freie Funktionen', modulTyp: 'frei' },
  { key: 'multi_therapeuten', label: 'Multi-Therapeuten', description: 'Patientenzuweisung, Workload-Übersicht, Vertretung', category: 'Freie Funktionen', modulTyp: 'frei' },
  { key: 'bls_ernaehrungsanalyse', label: 'BLS Nährwertanalyse', description: 'Detaillierte Kalorien- und Makroanalyse mit BLS-Datenbank', category: 'Freie Funktionen', modulTyp: 'frei' },
  { key: 'rezeptanalyse', label: 'Rezept-Nährwertanalyse', description: 'Rezept als Freitext eingeben → automatische Makro- und Kalorienberechnung (USDA-Daten)', category: 'Freie Funktionen', requires: ['ernaehrungsplaene'], modulTyp: 'frei' },
  { key: 'fhir_export', label: 'FHIR-Export (HL7 R4)', description: 'GNCP-Dokumentation als strukturierter FHIR NutritionOrder exportieren – für KIS-Integration und gematik-Konformität', category: 'Freie Funktionen', requires: ['integrationen'], modulTyp: 'frei' },
  { key: 'klinik_entlassbrief_ki', label: 'KI-Entlassbrief-Assistent', description: 'KI-generierte Entwurfs-Abschnitte im Entlassbrief (Mistral, EU-gehostet, DSGVO-konform)', category: 'Freie Funktionen', requires: ['klinik_modul'], modulTyp: 'frei' },
  // ─── Phase 3: Specialist Modules ───
  {
    key: 'klinik_its', label: 'ITS/ICU-Ernährung',
    description: 'Täglicher SOFA-Score, ICU-Energiebedarf mit Propofol-/Dextrose-Abzug (Harris-Benedict + Stressfaktor)',
    category: 'Freie Funktionen', requires: ['klinik_modul'], modulTyp: 'frei',
    price_monthly: 29, recommended_for: ['klinik'], is_new: false, icon: 'Activity',
  },
  {
    key: 'klinik_onkologie', label: 'Onkologie-Ernährung',
    description: 'PG-SGA Screening nach ESPEN-Leitlinie Onkologie: Kategorie A/B/C + Triage-Empfehlung',
    category: 'Freie Funktionen', requires: ['klinik_modul'], modulTyp: 'frei',
    price_monthly: 29, recommended_for: ['klinik'], is_new: false, icon: 'Ribbon',
  },
  {
    key: 'klinik_geriatrie', label: 'Geriatrie-Modul',
    description: 'SARC-F Sarkopenie-Screening, Polypharmazie-Hinweise (≥5 Medikamente) + MNA-Verlaufstabelle',
    category: 'Freie Funktionen', requires: ['klinik_modul'], modulTyp: 'frei',
    price_monthly: 19, recommended_for: ['klinik'], is_new: true, icon: 'HeartPulse',
  },
  // ─── KI-Companion (Basis) ───
  {
    key: 'ai_native', label: 'KI-Companion',
    description: 'KI-native Plattform: Hintergrund-Watcher (Refeeding, Gewichtstrend …), Suggestion-Inbox im Header, Capability-Pipeline mit Audit. DSGVO-konform, Mistral-EU-gehostet. Pro Org deaktivierbar.',
    category: 'Basis', modulTyp: 'basis',
    price_monthly: null, recommended_for: ['praxis', 'klinik'], is_new: false, icon: 'Sparkles',
  },
  // ─── Flow-Redesign (Default-Rückgrat, pro Org abschaltbar) ───
  {
    key: 'ui_flow_mode', label: 'Flow-Modus',
    description: 'Aufgabenfokussiertes Default-Interface: Timeline (Heute), Ambient-Voice-Konsultation, Linked-Evidence-Review. Pro Org deaktivierbar (dann klassische UI).',
    category: 'Basis', modulTyp: 'basis',
    price_monthly: null, recommended_for: ['praxis', 'klinik'], is_new: false, icon: 'Zap',
  },
  // ─── AI-First Chat-Dashboard (Beta) ───
  {
    key: 'ui_ai_first', label: 'AI-First Chat-Dashboard (Beta)',
    description: 'AI-Suggestions erscheinen als Smart Cards im Chat-Feed statt als Bell-Popover. Morning Briefing automatisch.',
    category: 'Basis', modulTyp: 'basis',
    price_monthly: null, recommended_for: ['praxis', 'klinik'], is_new: true, icon: 'MessageSquare',
    preview_images: ['/screenshots/therapist-dashboard.svg'],
  },
];

// Keep NAV_FEATURES and PATIENT_FEATURES for backwards compat in Layout + PatientDetail
export const NAV_FEATURES = [
  'rechnungen','ernaehrungsplaene','sitzungsaufnahme','telemedizin',
  'warteliste','gruppen','integrationen',
] as const;

export const PATIENT_FEATURES = [
  'ernaehrungsplan_patient','tagebuch','klinik_modul','klinik_mna_must',
  'klinik_laborwerte','klinik_iddsi','klinik_pflege','klinik_glim','laborwerte',
  'diaetanamnese','aufgaben','fragebögen','portal_uploads',
] as const;

// Fallback: Praxis-Kern als Default
export const DEFAULT_FEATURES: Feature[] = [...FEATURE_PLANS.praxis];

export type SettingType = 'praxis' | 'klinik';
